import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { useRealmApp } from "src/store/RealmApp";
import { useAccessToken } from "../useAccessToken";
import { ProjectsV2FilterGroup } from "../Services/Projects/projects";
import { ParentStatus } from "../General/status-groups";

export const utilizationKeys = {
  utilizationTrend: (startDate: string, endDate: string, aggregation: string) =>
    ["utilization", startDate, endDate, aggregation] as const,
  userTimeTrackingAnalytics: (startDate: string, endDate: string) =>
    ["userTimeTrackingAnalytics", startDate, endDate] as const,
  timesheetSubmissions: (startDate: string, endDate: string) =>
    ["timesheetSubmissions", startDate, endDate] as const,
  projectTimeTrackingAnalytics: (filter: ProjectsV2FilterGroup) =>
    ["projectTimeTrackingAnalytics", filter] as const,
};

export type TimesheetSubmissionData = {
  _id: string;
  name: string;
  email: string;
  periods: {
    period: number;
    periodStartDate: string;
    totalTrackedTime: number;
    totalBilledTrackedTime: number;
    submissionStatus: "submitted" | "not-submitted";
    approvalStatus: "approved" | "rejected" | "not-submitted" | "pending";
    // submission: "approved" | "submitted" | "rejected" | "not-submitted";
  }[];
};

export type UtilizationData = {
  _id: string;
  name: string;
  email: string;
  periods: {
    period: number;
    periodStartDate: string;
    totalTrackedTime: number;
    totalBilledTrackedTime: number;
    totalCapacity: number;
  }[];
};

export type UserTimeTrackingAnalyticsData = {
  instanceId: string;
  startDate: string;
  endDate: string;
  totalCapacity: number;
  totalAllocatedTime: number;
  totalTrackedTime: number;
  totalBillableTime: number;
  totalNonBillableTime: number;
  totalFixedFee: number;
  totalBudgetedTime: number;
  totalActualProjectCost: number;
  // totalOverrunCost: number;
  netUtilization: number;
  grossUtilization: number;
  users: {
    userId: string;
    name: string;
    email: string;
    capacity: number;
    allocatedTime: number;
    trackedTime: number;
    grossUtilization: number;
    billableTime: number;
    nonBillableTime: number;
    netUtilization: number;
    // hourlyRate: {
    //   value: number;
    //   currency: string;
    // };
    categoryTimes: {
      categoryId: string;
      categoryName: string;
      time: number;
    }[];
    projects: {
      projectId: string;
      name: string;
      startDate?: string;
      endDate?: string;
      fixedFee?: {
        value: number;
        currency: string;
      };
      budgetedTime?: number;
      allocatedTime: number;
      plannedUtilization: number;
      trackedTime: number;
      grossUtilization: number;
      billableTime: number;
      nonBillableTime: number;
      netUtilization: number;
      actualProjectCost: number;
    }[];
  }[];
};

export type ProjectTimeTrackingAnalyticsData = {
  _id: string;
  name: string;
  customer: {
    _id: string;
    name: string;
  };
  billingDetails?: {
    type: 'fixed-fee' | 'time-and-material' | "non-billable";
    fixedFee?: {
      value: number;
      currency: string;
    };
    budgetedRevenue?: {
      value: number;
      currency: string;
    };
    budgetedTime?: number;
  };
  assignedStartDate?: string;
  assignedEndDate?: string;
  completedTasks: number;
  totalTasks: number;
  taskStatusBreakdown: Array<{
    name: string;
    parentStatus: ParentStatus;
    taskCount: number;
  }>;
  trackedTime: {
    total: number;
    billable: number;
    nonBillable: number;
  };
  allocatedTime: number;
  cost: number;
  revenue: number;
  createdAt: string;
  updatedAt: string;
};

export const useGetTimesheetSubmissions = (
  startDate: string,
  endDate: string
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    utilizationKeys.timesheetSubmissions(startDate, endDate),
    async (): Promise<TimesheetSubmissionData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/timesheetSubmissionsReport`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            startDate,
            endDate,
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetUtilization = (
  startDate: string,
  endDate: string,
  aggregation: "weekly" | "monthly" | "quarterly" | "daily"
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    utilizationKeys.utilizationTrend(startDate, endDate, aggregation),
    async (): Promise<UtilizationData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/utilizationReport`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            startDate,
            endDate,
            aggregation,
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetUserTimeTrackingAnalytics = (
  startDate: string,
  endDate: string
) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    utilizationKeys.userTimeTrackingAnalytics(startDate, endDate),
    async (): Promise<UserTimeTrackingAnalyticsData> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/userTimeTrackingAnalytics`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            startDate,
            endDate,
            instanceId: app.currentUser.customData.instanceId.$oid,
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

export const useGetProjectTimeTrackingAnalytics = (filter: ProjectsV2FilterGroup) => {
  const app = useRealmApp();
  const getValidAccessToken = useAccessToken();
  return useQuery(
    utilizationKeys.projectTimeTrackingAnalytics(filter),
    async (): Promise<ProjectTimeTrackingAnalyticsData[]> => {
      const accessToken = await getValidAccessToken();
      const res = await axios.get(
        `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/resourceManagementOverviewForProjects`,
        {
          headers: {
            Authorization: `Bearer ${accessToken}`,
            Accept: "application/json",
          },
          params: {
            instanceId: app.currentUser.customData.instanceId.$oid,
            projectFilter: JSON.stringify(filter),
          },
        }
      );
      return res.data;
    },
    {
      staleTime: Infinity,
    }
  );
};

// export const useGetProjectTimeTrackingAnalytics = () => {
//   const app = useRealmApp();
//   const getValidAccessToken = useAccessToken();
//   return useQuery(
//     utilizationKeys.utilizationDeepDive(),
//     async (): Promise<ProjectTimeTrackingAnalyticsData> => {
//       const accessToken = await getValidAccessToken();
//       const res = await axios.get(
//         `https://us-east-1.aws.data.mongodb-api.com/app/${process.env.REACT_APP_APP_ID}/endpoint/projectTimeTrackingAnalytics`,
//         {
//           headers: {
//             Authorization: `Bearer ${accessToken}`,
//             Accept: "application/json",
//           },
//           params: {
//             // startDate,
//             // endDate,
//             instanceId: app.currentUser.customData.instanceId.$oid,
//           },
//         }
//       );
//       return res.data;
//     },
//     {
//       staleTime: Infinity,
//     }
//   );
// };
